import { useEffect, useImperativeHandle, useRef, type Ref } from 'react';

import { TURNSTILE_SITE_KEY } from '../config/public-config.js';

/**
 * Cloudflare Turnstile, invisible mode, on room creation only.
 *
 * Deliberate constraints:
 *   * it renders nothing and loads nothing when no site key is configured, so local
 *     development and the test suite never touch a third-party origin;
 *   * it exists only on the public homepage — a participant answering a private room is
 *     never challenged, which is what the specification asks for;
 *   * failing to get a token is not fatal here. The server decides whether a token is
 *     required; the client's job is only to offer one.
 */

export interface TurnstileHandle {
  getToken: () => Promise<string | undefined>;
  reset: () => void;
}

interface TurnstileApi {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      appearance?: 'always' | 'execute' | 'interaction-only';
      size?: 'normal' | 'compact' | 'flexible';
      retry?: 'auto' | 'never';
      'retry-interval'?: number;
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
    },
  ) => string;
  reset: (widgetId: string) => void;
  getResponse: (widgetId: string) => string | undefined;
  execute: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
/**
 * How long a submission waits for a challenge token before giving up and posting anyway.
 *
 * Deliberately short. If the token never arrives the server decides what to do, and the
 * person sees a clear message immediately rather than a button that appears frozen. An
 * earlier 12-second value made a failed challenge look like a broken site.
 */
const TOKEN_TIMEOUT_MS = 4500;

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();

  const existing = document.querySelector<HTMLScriptElement>(`script[src^="${SCRIPT_URL}"]`);
  if (existing) {
    return new Promise((resolve) => {
      existing.addEventListener('load', () => {
        resolve();
      });
      existing.addEventListener('error', () => {
        resolve();
      });
    });
  }

  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = `${SCRIPT_URL}?render=explicit`;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => {
      resolve();
    });
    script.addEventListener('error', () => {
      resolve();
    });
    document.head.append(script);
  });
}

export function Turnstile({ ref }: { ref?: Ref<TurnstileHandle> }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenRef = useRef<string | undefined>(undefined);
  const enabled = TURNSTILE_SITE_KEY !== '';

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    void loadScript().then(() => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        /*
         * Managed mode with the default appearance.
         *
         * `interaction-only` was tried first and is a trap: it keeps the widget hidden,
         * so when Turnstile decides a human click is needed there is no checkbox to
         * click. The challenge silently never resolves, no token is issued, and — with
         * verification required server-side — nobody can create a room. Observed on the
         * live site before launch.
         *
         * The default solves invisibly for most visitors and reveals a checkbox only
         * when it genuinely needs one, which is both least-intrusive and not a dead end.
         */
        size: 'flexible',
        retry: 'auto',
        'retry-interval': 2000,
        callback: (token) => {
          tokenRef.current = token;
        },
        'expired-callback': () => {
          tokenRef.current = undefined;
        },
        'error-callback': () => {
          tokenRef.current = undefined;
        },
      });
    });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  useImperativeHandle(
    ref,
    () => ({
      async getToken() {
        if (!enabled) return undefined;
        if (tokenRef.current) return tokenRef.current;

        const widgetId = widgetIdRef.current;
        if (!widgetId || !window.turnstile) return undefined;

        // In the default render mode the widget solves itself and fires the callback, so
        // `execute` is unnecessary and can throw. It is only needed when the widget was
        // configured for explicit execution. Either way the token arrives via the
        // callback or `getResponse`, so a failure here must not stop the submission.
        try {
          window.turnstile.execute(widgetId);
        } catch {
          /* already executing, or not in explicit-execution mode */
        }

        // Wait briefly for the challenge to settle rather than blocking the submit
        // indefinitely; the server rejects a missing token if it requires one.
        const deadline = Date.now() + TOKEN_TIMEOUT_MS;
        while (Date.now() < deadline) {
          const response = window.turnstile.getResponse(widgetId);
          if (response) {
            tokenRef.current = response;
            return response;
          }
          await new Promise((resolve) => setTimeout(resolve, 150));
        }
        return undefined;
      },
      reset() {
        tokenRef.current = undefined;
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }),
    [enabled],
  );

  if (!enabled) return null;
  // Reserves no height until the widget actually appears, so the form does not jump for
  // the majority of visitors who never see a challenge.
  return <div className="turnstile" ref={containerRef} />;
}
